import React from 'react';
import PropTypes from 'prop-types';
import round from '../../../helpers/round';
import gibsonGraph from '../../../assets/calculations/piPhi/gibson 1953.JPG';

const GRAPH_WIDTH = 380;
const IMAGE = { width: 782, height: 522 };
const PLOT = {
  left: 109,
  right: 740,
  top: 23,
  bottom: 426,
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const mapPiToX = (pi) => {
  const normalized = clamp(pi, 0, 120) / 120;
  return PLOT.left + normalized * (PLOT.right - PLOT.left);
};

const mapPhiToY = (phi) => {
  const normalized = clamp(phi, 0, 40) / 40;
  return PLOT.bottom - normalized * (PLOT.bottom - PLOT.top);
};

const buildGraph = (pi, phi, phiRes) => {
  const height = (GRAPH_WIDTH * IMAGE.height) / IMAGE.width;
  const scale = GRAPH_WIDTH / IMAGE.width;
  const x = mapPiToX(pi) * scale;
  const yPhi = mapPhiToY(phi) * scale;
  const yPhiRes = mapPhiToY(phiRes) * scale;
  const left = PLOT.left * scale;
  const bottom = PLOT.bottom * scale;

  return {
    width: GRAPH_WIDTH,
    height,
    x,
    verticalX: x - 3,
    yPhi,
    yPhiRes,
    left,
    bottom,
    phiLabelX: left + 4,
    phiLabelY: yPhi - 6,
    phiResLabelX: left + 4,
    phiResLabelY: yPhiRes + 14,
  };
};

const ClayGibsonGraphSvg = ({ pi, phi, phiRes, showOverlay }) => {
  const piValue = Number(pi);
  const phiValue = Number(phi);
  const phiResValue = Number(phiRes);
  const graph = buildGraph(piValue, phiValue, phiResValue);

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${graph.width} ${graph.height}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Gibson PI versus effective and residual effective friction angle graph"
      style={{ display: 'block', width: '100%', height: 'auto', aspectRatio: `${graph.width} / ${graph.height}` }}
    >
      <image href={gibsonGraph} x="0" y="0" width={graph.width} height={graph.height} />
      {showOverlay && (
        <>
          <g stroke="#b33a3a" fill="none" strokeWidth="2" vectorEffect="non-scaling-stroke">
            <line x1={graph.verticalX} y1={graph.yPhi} x2={graph.verticalX} y2={graph.bottom} />
            <polyline
              points={`${graph.verticalX - 3},${graph.yPhi + 5} ${graph.verticalX},${graph.yPhi} ${graph.verticalX + 3},${graph.yPhi + 5}`}
            />
            <line x1={graph.left} y1={graph.yPhi} x2={graph.x} y2={graph.yPhi} />
            <polyline
              points={`${graph.left + 6},${graph.yPhi - 3} ${graph.left + 1},${graph.yPhi} ${graph.left + 6},${graph.yPhi + 3}`}
            />
          </g>
          <g stroke="#c57c00" fill="none" strokeWidth="2" vectorEffect="non-scaling-stroke">
            <line x1={graph.left} y1={graph.yPhiRes} x2={graph.x} y2={graph.yPhiRes} />
            <polyline
              points={`${graph.left + 6},${graph.yPhiRes - 3} ${graph.left + 1},${graph.yPhiRes} ${graph.left + 6},${graph.yPhiRes + 3}`}
            />
          </g>
        </>
      )}
      {showOverlay && (
        <>
          <text x={graph.phiLabelX} y={graph.phiLabelY} fill="#b33a3a" fontSize="10" fontWeight="700">
            φ&apos;={round(phiValue, 2)} deg
          </text>
          <text x={graph.phiResLabelX} y={graph.phiResLabelY} fill="#c57c00" fontSize="10" fontWeight="700">
            φ&apos;r={round(phiResValue, 2)} deg
          </text>
        </>
      )}
    </svg>
  );
};

ClayGibsonGraphSvg.propTypes = {
  pi: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  phi: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  phiRes: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  showOverlay: PropTypes.bool,
};

ClayGibsonGraphSvg.defaultProps = {
  showOverlay: false,
};

export default React.memo(ClayGibsonGraphSvg);
